import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api, formatApiError } from "../../lib/apiClient";
import ImageUpload from "../../components/ImageUpload";

/**
 * Generic gallery admin manager.
 *
 * @param endpoint     e.g. "/admin/numbercake-gallery" or "/admin/cookie-perso-gallery"
 * @param title        page H1
 * @param description  one-liner under the title
 * @param testidPrefix data-testid prefix
 * @param itemLabel    e.g. "number cake" or "cookie personnalisé"
 */
export default function GalleryManager({
    endpoint,
    title,
    description,
    testidPrefix = "gallery",
    itemLabel = "photo",
}) {
    const [items, setItems] = useState([]);
    const [imageUrl, setImageUrl] = useState("");
    const [caption, setCaption] = useState("");

    const fetchAll = useCallback(async () => {
        try {
            const { data } = await api.get(endpoint);
            setItems(data);
        } catch (_e) {
            toast.error("Erreur de chargement");
        }
    }, [endpoint]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const create = async (e) => {
        e.preventDefault();
        if (!imageUrl) {
            toast.error("Téléverse d'abord une photo.");
            return;
        }
        try {
            // Place new item before all existing ones (smallest sort_order)
            const minOrder = items.length
                ? Math.min(...items.map((x) => x.sort_order ?? 0))
                : 0;
            await api.post(endpoint, {
                image_url: imageUrl,
                caption,
                sort_order: minOrder - 1,
                active: true,
            });
            toast.success("Photo ajoutée");
            setImageUrl("");
            setCaption("");
            fetchAll();
        } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail) || "Erreur");
        }
    };

    const move = async (it, dir) => {
        const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);
        const idx = sorted.findIndex((x) => x.id === it.id);
        const j = dir === "up" ? idx - 1 : idx + 1;
        if (j < 0 || j >= sorted.length) return;
        const a = sorted[idx];
        const b = sorted[j];
        await Promise.all([
            api.put(`${endpoint}/${a.id}`, { sort_order: b.sort_order }),
            api.put(`${endpoint}/${b.id}`, { sort_order: a.sort_order }),
        ]);
        fetchAll();
    };

    const updateCaption = async (id, newCaption) => {
        try {
            await api.put(`${endpoint}/${id}`, { caption: newCaption });
            toast.success("Légende mise à jour");
            fetchAll();
        } catch (_e) {
            toast.error("Erreur");
        }
    };

    const toggleActive = async (it) => {
        try {
            await api.put(`${endpoint}/${it.id}`, { active: !it.active });
            fetchAll();
        } catch (_e) {
            toast.error("Erreur");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cette photo ?")) return;
        try {
            await api.delete(`${endpoint}/${id}`);
            toast.success("Supprimée");
            fetchAll();
        } catch (_e) {
            toast.error("Erreur");
        }
    };

    return (
        <div data-testid={`admin-${testidPrefix}-page`}>
            <Toaster richColors position="top-center" />
            <p className="section-tag">administration</p>
            <h1 className="font-serif-display text-4xl text-[#2A1408] tracking-tight">
                {title}
            </h1>
            <p className="text-[#5C3D2E] mt-2">{description}</p>

            <section className="brand-card p-6 mt-6">
                <h2 className="font-display text-2xl text-[#2A1408] mb-4">
                    Nouvelle photo
                </h2>
                <form
                    onSubmit={create}
                    className="space-y-4"
                    data-testid={`${testidPrefix}-form`}
                >
                    <ImageUpload
                        value={imageUrl}
                        onChange={setImageUrl}
                        label="Photo (carré 1:1 recommandé)"
                    />
                    <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Légende (facultatif)
                        <input
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder={`ex: ${itemLabel} thème jungle`}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid={`${testidPrefix}-caption`}
                        />
                    </label>
                    <button
                        type="submit"
                        className="brand-btn"
                        data-testid={`${testidPrefix}-submit`}
                    >
                        Ajouter à la galerie
                    </button>
                </form>
            </section>

            <section className="mt-8">
                <h2 className="font-display text-2xl text-[#2A1408] mb-4">
                    Photos publiées ({items.length})
                </h2>
                {items.length === 0 ? (
                    <p className="text-[#5C3D2E]">Aucune photo pour l'instant.</p>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[...items]
                            .sort((a, b) => a.sort_order - b.sort_order)
                            .map((it) => (
                                <article
                                    key={it.id}
                                    className={`brand-card overflow-hidden ${
                                        !it.active ? "opacity-50" : ""
                                    }`}
                                    data-testid={`admin-${testidPrefix}-${it.id}`}
                                >
                                    <div className="aspect-square overflow-hidden bg-[#F5E6D3]">
                                        <img
                                            src={
                                                it.image_url?.startsWith("/api/")
                                                    ? `${process.env.REACT_APP_BACKEND_URL}${it.image_url}`
                                                    : it.image_url
                                            }
                                            alt={it.caption || itemLabel}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    {it.caption !== undefined && (
                                        <CaptionEditor
                                            initial={it.caption || ""}
                                            onSave={(val) => updateCaption(it.id, val)}
                                        />
                                    )}
                                    <div className="flex items-center justify-between border-t border-[#E4D1BB] px-3 py-2 gap-2">
                                        <div className="flex gap-1">
                                            <button
                                                type="button"
                                                onClick={() => move(it, "up")}
                                                className="text-[#6B4226] hover:text-[#2A1408] p-1"
                                                aria-label="Monter"
                                            >
                                                <ArrowUp size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => move(it, "down")}
                                                className="text-[#6B4226] hover:text-[#2A1408] p-1"
                                                aria-label="Descendre"
                                            >
                                                <ArrowDown size={14} />
                                            </button>
                                        </div>
                                        <div className="flex gap-1">
                                            <button
                                                type="button"
                                                onClick={() => toggleActive(it)}
                                                className="text-[#6B4226] hover:text-[#2A1408] p-1"
                                                aria-label={it.active ? "Masquer" : "Afficher"}
                                            >
                                                {it.active ? (
                                                    <Eye size={14} />
                                                ) : (
                                                    <EyeOff size={14} />
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => remove(it.id)}
                                                className="text-red-700 hover:text-red-900 p-1"
                                                aria-label="Supprimer"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function CaptionEditor({ initial, onSave }) {
    const [val, setVal] = useState(initial);
    const [dirty, setDirty] = useState(false);
    return (
        <div className="px-2 py-2 border-t border-[#E4D1BB]">
            <input
                value={val}
                onChange={(e) => {
                    setVal(e.target.value);
                    setDirty(e.target.value !== initial);
                }}
                onBlur={() => {
                    if (dirty) {
                        onSave(val);
                        setDirty(false);
                    }
                }}
                placeholder="Légende"
                className="w-full bg-transparent text-sm text-[#3D1F0F] focus:outline-none focus:bg-white focus:border-b-2 focus:border-[#D6552C] px-1 py-1"
            />
            {dirty && (
                <p className="text-[10px] text-[#B07B66] mt-0.5">
                    Enter ou clic ailleurs pour sauvegarder
                </p>
            )}
        </div>
    );
}
